import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Mail';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import LoadingFallback from '../components/LoadingFallback';
import StatusChip from '../components/StatusChip';
import FormSection from '../components/FormSection';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { Meet, Notification, Registration, MeetEvent, SchedulePreview } from '../api/types';

interface PreviewResponse {
  previews: SchedulePreview[];
  recipient_count: number;
}

export default function NotificationsPage() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const { confirm, toast } = useFeedback();
  const [tab, setTab] = useState(0);

  const meetsQuery = useQuery<Meet[]>({
    queryKey: ['meets'],
    queryFn: async () => (await api.get('/meets')).data,
  });
  const notifsQuery = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get('/notifications')).data,
  });

  const initialMeet = params.get('meet');
  const [meetId, setMeetId] = useState<number | ''>(initialMeet ? Number(initialMeet) : '');
  useEffect(() => {
    if (initialMeet && meetId === '') setMeetId(Number(initialMeet));
  }, [initialMeet, meetId]);

  const eventsQuery = useQuery<MeetEvent[]>({
    queryKey: ['meets', meetId, 'events'],
    enabled: !!meetId,
    queryFn: async () => (await api.get(`/meet-events/by-meet/${meetId}`)).data,
  });

  const eventRegistrationsQuery = useQuery<{ regs: Registration[]; meetId: number | '' }>({
    queryKey: ['meets', meetId, 'registrations-flat'],
    enabled: !!meetId && !!eventsQuery.data,
    queryFn: async () => {
      if (!eventsQuery.data) return { regs: [], meetId };
      const responses = await Promise.all(
        eventsQuery.data.map((ev) =>
          api.get<Registration[]>(`/registrations/by-meet-event/${ev.id}`).then((r) => r.data),
        ),
      );
      return { regs: responses.flat(), meetId };
    },
  });

  const studentMap = useMemo(() => {
    const m = new Map<number, { id: number; name: string }>();
    for (const r of eventRegistrationsQuery.data?.regs ?? []) {
      m.set(r.student_id, { id: r.student_id, name: r.student.full_name });
    }
    return m;
  }, [eventRegistrationsQuery.data]);

  const allIds = useMemo(() => Array.from(studentMap.keys()), [studentMap]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    setSelectedIds(allIds);
  }, [allIds]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewResponse | null>(null);
  const [activePreview, setActivePreview] = useState(0);
  const [viewing, setViewing] = useState<Notification | null>(null);

  const previewMutation = useMutation({
    mutationFn: async (): Promise<PreviewResponse> => {
      const { data } = await api.post('/notifications/parent-schedule/preview', {
        meet_id: meetId,
        student_ids: selectedIds,
      });
      return data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setActivePreview(0);
      setPreviewOpen(true);
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const sendMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post('/notifications/parent-schedule/send', {
          meet_id: meetId,
          student_ids: selectedIds,
        })
      ).data,
    onSuccess: (data) => {
      toast({
        message: `Sent ${data.sent} email${data.sent === 1 ? '' : 's'}${data.failed ? `, ${data.failed} failed` : ''}.`,
        severity: data.failed ? 'warning' : 'success',
      });
      setPreviewOpen(false);
      setPreviewData(null);
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  const handleMeetChange = (val: string) => {
    const id = val ? Number(val) : '';
    setMeetId(id);
    if (id) {
      params.set('meet', String(id));
      setParams(params, { replace: true });
    } else {
      params.delete('meet');
      setParams(params, { replace: true });
    }
  };

  const toggleAll = () => {
    setSelectedIds(selectedIds.length === allIds.length ? [] : allIds);
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <Box>
      <PageHeader
        title="Parent Emails"
        subtitle="Pick a meet, choose registered athletes, preview linked-parent schedules, then send."
      />

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab label="Send schedule emails" id="tab-send" />
          <Tab label="History" id="tab-history" />
        </Tabs>

        {tab === 0 && (
          <CardContent>
            <FormSection title="1. Pick a meet" description="Only meets with events and entries can produce parent schedules.">
              <TextField
                select
                label="Meet"
                fullWidth
                value={meetId}
                onChange={(e) => handleMeetChange(e.target.value)}
              >
                <MenuItem value="">— Select —</MenuItem>
                {(meetsQuery.data ?? []).map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    <ListItemText primary={m.name} secondary={dayjs(m.start_date).format('MMM D, YYYY')} />
                  </MenuItem>
                ))}
              </TextField>
            </FormSection>

            <FormSection title="2. Select athletes" description="Each linked parent gets one email summarizing every selected child. Athletes without parent emails will be skipped.">
              {!meetId ? (
                <Typography color="text.secondary">Pick a meet to see registered athletes.</Typography>
              ) : eventRegistrationsQuery.isLoading ? (
                <LoadingFallback height={120} />
              ) : allIds.length === 0 ? (
                <Alert severity="info">No athletes are registered in this meet yet. Go to Prepare Meet, add events, and save entries first.</Alert>
              ) : (
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedIds.length === allIds.length}
                        indeterminate={selectedIds.length > 0 && selectedIds.length < allIds.length}
                        onChange={toggleAll}
                        inputProps={{ 'aria-label': 'Select all athletes' }}
                      />
                    }
                    label={`${selectedIds.length} of ${allIds.length} selected`}
                  />
                  <Grid container spacing={1}>
                    {Array.from(studentMap.values()).map((s) => (
                      <Grid item xs={12} sm={6} md={4} key={s.id}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedIds.includes(s.id)}
                              onChange={() => toggleOne(s.id)}
                              inputProps={{ 'aria-label': `Select ${s.name}` }}
                            />
                          }
                          label={s.name}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </FormSection>

            <FormSection title="3. Preview and send" description="Preview is required so you can confirm exactly which parent emails Baton found.">
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  startIcon={<EmailIcon />}
                  disabled={!meetId || selectedIds.length === 0 || previewMutation.isPending}
                  onClick={() => previewMutation.mutate()}
                  aria-label="Preview parent emails"
                >
                  {previewMutation.isPending ? 'Building preview…' : 'Preview emails'}
                </Button>
              </Stack>
            </FormSection>
          </CardContent>
        )}

        {tab === 1 && (
          <CardContent>
            {notifsQuery.isLoading ? (
              <LoadingFallback />
            ) : (notifsQuery.data ?? []).length === 0 ? (
              <EmptyState title="No parent emails sent yet" description="Schedule emails you preview and send will appear here with delivery status." />
            ) : (
              <Table size="small" aria-label="Parent emails history">
                <TableHead>
                  <TableRow>
                    <TableCell>Sent</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(notifsQuery.data ?? []).map((n) => (
                    <TableRow key={n.id} hover>
                      <TableCell>
                        {n.sent_at
                          ? dayjs(n.sent_at).format('MMM D, h:mm A')
                          : dayjs(n.created_at).format('MMM D, h:mm A')}
                      </TableCell>
                      <TableCell>{n.recipient_email}</TableCell>
                      <TableCell>{n.subject}</TableCell>
                      <TableCell><StatusChip status={n.status} /></TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => setViewing(n)}
                          aria-label={`View email to ${n.recipient_email}`}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="preview-dialog-title"
      >
        <DialogTitle id="preview-dialog-title">
          Preview {previewData ? `(${activePreview + 1} of ${previewData.previews.length})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {previewData && previewData.previews.length > 0 ? (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {previewData.previews.map((p, idx) => (
                  <Chip
                    key={p.parent_id}
                    label={p.parent_email}
                    color={idx === activePreview ? 'primary' : 'default'}
                    variant={idx === activePreview ? 'filled' : 'outlined'}
                    onClick={() => setActivePreview(idx)}
                    aria-label={`Show preview for ${p.parent_email}`}
                  />
                ))}
              </Stack>
              <Box>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="subtitle1">{previewData.previews[activePreview].subject}</Typography>
              </Box>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 480,
                  overflow: 'auto',
                  bgcolor: 'background.default',
                }}
                dangerouslySetInnerHTML={{ __html: previewData.previews[activePreview].body_html }}
              />
            </Stack>
          ) : (
            <Alert severity="info">No previews available - none of the selected athletes have linked parent emails.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} aria-label="Cancel send">Cancel</Button>
          <Button
            variant="contained"
            disabled={!previewData || previewData.previews.length === 0 || sendMutation.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: 'Send emails?',
                message: `This will send ${previewData?.previews.length ?? 0} email${
                  previewData?.previews.length === 1 ? '' : 's'
                } now.`,
                confirmLabel: 'Send',
              });
              if (ok) sendMutation.mutate();
            }}
            aria-label="Send all emails"
            startIcon={<EmailIcon />}
          >
            {sendMutation.isPending ? 'Sending…' : `Send ${previewData?.previews.length ?? 0} email(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!viewing}
        onClose={() => setViewing(null)}
        fullWidth
        maxWidth="md"
        aria-labelledby="view-email-title"
      >
        <DialogTitle id="view-email-title">{viewing?.subject}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            To {viewing?.recipient_email} · {viewing?.sent_at ? dayjs(viewing.sent_at).format('MMM D, YYYY h:mm A') : 'not sent'}
          </Typography>
          {viewing?.error_message && (
            <Alert severity="error" sx={{ mb: 2 }}>{viewing.error_message}</Alert>
          )}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              bgcolor: 'background.default',
            }}
            dangerouslySetInnerHTML={{ __html: viewing?.body_html || '' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewing(null)} variant="contained" aria-label="Close">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
